import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import styled from "styled-components";
import theme from "../../theme";
import {
  CustomerRegisterCrudType,
  SystemSetting,
} from "../../types/types";
import InputField from "./inputField";
import usePostCustomer from "../../hooks/usePostCustomer";
import { isOnlyNumber } from "../../utils/util";
import AlertModal from "../alertModal";
import { WarningIcon } from "../../assets/icons/icons";
import { Button } from "../common/Button";
import { ModalLayout } from "../common/ModalLayout";
import { useSnackbar } from "notistack";
import { useWindowSize } from "@reactuses/core";
import { useI18n } from "../../locales";

const { md } = theme.screen;

const CustomerRegisterModal: React.FC<{
  open: boolean;
  onPressNo: () => void;
  systemSetting: SystemSetting | null;
}> = (props) => {
  const { open, onPressNo, systemSetting } = props;
  const { width } = useWindowSize();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useI18n();
  const [pwError, setPwError] = useState("");
  const [checkPwSpace, setCheckPwSpace] = useState(false);
  const queryClient = useQueryClient();
  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [form, setForm] = useState<CustomerRegisterCrudType>({
    address: "",
    address_detail: "",
    name: "",
    id: "",
    pw: "",
    group_id: "00000",
    illness: "",
  });
  const [errors, setErrors] = useState({
    birthError: false,
    genderError: false,
  });

  const body = {
    address: form.address,
    address_detail: form.address_detail,
    name: form.name,
    id: form.id,
    pw: form.pw /*SHA256(form.pw).toString()*/,
    group_id: form.group_id,
    illness: form.illness,
  };

  const { mutate: postCustomer, isPending } = usePostCustomer();

  const handlePost = () => {
    postCustomer(body, {
      onSuccess: (data) => {
        closeToModal();
        queryClient.invalidateQueries({
          queryKey: ["Customer"],
        });
        enqueueSnackbar(t.manageCustomer.registerSuccess, {
          variant: "success",
        });
        return data;
      },
    });
  };

  // 모달 닫기 함수
  const closeToModal = () => {
    setOpenConfirmModal(false);
    setForm({
      address: "",
      address_detail: "",
      name: "",
      id: "",
      pw: "",
      group_id: "00000",
      illness: "",
    });
    onPressNo();
    setPwError("");
  };

  // 필수 입력 조건
  const isAllFilled =
    pwError === "" &&
    [form.name, form.id, form.address].every((el) => el !== "") &&
    !form.id.includes("-") &&
    form.id.length >= 10 &&
    // (form.pw.length === 0 || form.pw.length >= 8) &&
    form.pw.length >= 8 &&
    isOnlyNumber(form.id) &&
    !errors.birthError &&
    !errors.genderError;

  return (
    <ModalLayout
      open={open}
      onClose={() => setOpenConfirmModal(true)}
      width="520px"
      title={t.manageCustomer.registerInfoTitle}
    >
      <InputField
        form={form}
        setForm={setForm}
        pwError={pwError}
        setPwError={setPwError}
        checkPwSpace={checkPwSpace}
        setCheckPwSpace={setCheckPwSpace}
        errors={errors}
        setErrors={setErrors}
      />

      <BtnWrapper>
        <Button
          isDisabled={!isAllFilled || !systemSetting?.create}
          isLoading={isPending}
          onClick={() => {
            if (checkPwSpace || pwError) {
              enqueueSnackbar(t.manageCustomer.invalidPassword, {
                variant: "error",
              });
              return;
            } else {
              handlePost();
            }
          }}
          bgColor={theme.statusColor.MAIN}
          hoverColor={theme.statusColor.MAIN_HOVER}
        >
          {width <= md ? t.common.register : t.manageCustomer.registerComplete}
        </Button>
        <Button
          bgColor={theme.color.neutral_400}
          onClick={() => setOpenConfirmModal(true)}
          hoverColor={theme.color.neutral_500}
        >
          {t.common.cancel}
        </Button>
      </BtnWrapper>
      <AlertModal
        open={openConfirmModal}
        onPressNo={() => setOpenConfirmModal(false)}
        title={t.manageCustomer.cancelRegisterTitle}
        content={t.manageCustomer.cancelRegisterContent}
        confirmText={t.common.confirm}
        icon={<WarningIcon />}
        onPressYes={closeToModal}
      />
    </ModalLayout>
  );
};

const BtnWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 20px;
  margin: 0 50px;
  margin-top: 20px;

  button {
    width: 270px;
  }

  @media (max-width: ${md}px) {
    margin: 0px;
    margin-top: 20px;
  }
`;

export default CustomerRegisterModal;
